import { API_DOMAIN } from 'constants/infograph';
import { InteractionSubmissionData } from 'types/interaction.type';

export const INTERACTION_SUBMISSION_API_ENDPOINT = '/api/design/interactionSubmission';
export const INTERACTION_SUBMISSION_TOGGLE_API_ENDPOINT = '/api/design/interactionSubmissionToggle';

export type InteractionSubmissionRequest = {
  privateLinkId: string;
  submission: InteractionSubmissionData;
};

export type InteractionSubmissionToggleRequest = {
  /**
   * Design uuid - E1 format
   */
  originalEditorId: string;
  enabled: boolean;
};

export type InteractionSubmissionDownloadRequest = {
  /**
   * E2 design id
   */
  id: string;
  format: 'csv' | 'tsv';
};

export type InteractionSubmissionResponse = {
  status: string;
  message?: string;
};

/**
 * Save interaction submission data via infograph rest API
 *
 * POST /api/design/interactionSubmission
 */
export const interactionSubmission = async (
  data: InteractionSubmissionRequest,
): Promise<InteractionSubmissionResponse> => {
  const resp = await fetch(INTERACTION_SUBMISSION_API_ENDPOINT, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!resp.ok) {
    throw new Error('Submission fail');
  }

  return await resp.json();
};

/**
 * Toggle interaction submission flag via infograph rest API
 *
 * POST /api/design/interactionSubmissionToggle
 */
export const interactionSubmissionToggle = async ({
  originalEditorId,
  enabled,
}: InteractionSubmissionToggleRequest): Promise<InteractionSubmissionResponse> => {
  const resp = await fetch(INTERACTION_SUBMISSION_TOGGLE_API_ENDPOINT, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: originalEditorId, enabled }),
  });

  if (!resp.ok) {
    throw new Error('Fail to toggle submission permission');
  }

  return await resp.json();
};

/**
 * Download giving design's interaction submissions data in csv format
 *
 * Note: we may switch to use infograph proxy API for better security and tracking
 */
export const interactionSubmissionDownload = async (
  { id, format = 'csv' }: InteractionSubmissionDownloadRequest,
  token: string,
): Promise<Blob> => {
  const resp = await fetch(`${API_DOMAIN}/interactionSubmissionDownload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id,
      format,
    }),
  });

  if (!resp.ok) {
    throw new Error('Failed to download interaction submissions');
  }

  return await resp.blob();
};
